import { useState } from 'react';
import { Copy, Plus, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

interface Stop {
  color: string;
  pos: number;
}

type Type = 'linear' | 'radial' | 'conic';

export function GradientBuilder() {
  const [type, setType] = useState<Type>('linear');
  const [angle, setAngle] = useState(135);
  const [stops, setStops] = useState<Stop[]>([
    { color: '#6366f1', pos: 0 },
    { color: '#ec4899', pos: 100 },
  ]);

  const stopsCss = stops
    .slice()
    .sort((a, b) => a.pos - b.pos)
    .map((s) => `${s.color} ${s.pos}%`)
    .join(', ');
  const css =
    type === 'linear'
      ? `linear-gradient(${angle}deg, ${stopsCss})`
      : type === 'radial'
      ? `radial-gradient(circle at center, ${stopsCss})`
      : `conic-gradient(from ${angle}deg, ${stopsCss})`;

  function addStop() {
    setStops((s) => [...s, { color: '#ffffff', pos: 50 }]);
  }
  function removeStop(i: number) {
    if (stops.length <= 2) return;
    setStops((s) => s.filter((_, idx) => idx !== i));
  }
  function update(i: number, patch: Partial<Stop>) {
    setStops((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }

  return (
    <div>
      <ToolHeader
        toolId="gradient-builder"
        title="Gradient builder"
        description="Linear, radial, and conic gradients with multi-stop control. Copy as CSS, Tailwind, or SVG."
      />

      <Card className="!p-0 overflow-hidden mb-4">
        <div className="h-72" style={{ background: css }} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Card>
          <Tabs value={type} onValueChange={(v) => setType(v as Type)}>
            <TabsList className="w-full">
              <TabsTrigger value="linear" className="flex-1">Linear</TabsTrigger>
              <TabsTrigger value="radial" className="flex-1">Radial</TabsTrigger>
              <TabsTrigger value="conic" className="flex-1">Conic</TabsTrigger>
            </TabsList>
          </Tabs>
          {(type === 'linear' || type === 'conic') && (
            <Field label="Angle" hint={`${angle}°`} className="mt-4">
              <Slider value={angle} onChange={setAngle} max={360} showValue={false} />
            </Field>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-medium text-fg-muted">Stops</div>
            <Button size="sm" variant="ghost" onClick={addStop}>
              <Plus size={11} />
              Add
            </Button>
          </div>
          <div className="space-y-2">
            {stops.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="color"
                  value={s.color}
                  onChange={(e) => update(i, { color: e.target.value })}
                  className="!w-10 !h-9"
                />
                <Input
                  value={s.color}
                  onChange={(e) => update(i, { color: e.target.value })}
                  className="font-mono w-28"
                />
                <Input
                  type="number"
                  value={s.pos}
                  onChange={(e) =>
                    update(i, { pos: Math.max(0, Math.min(100, Number(e.target.value))) })
                  }
                  className="w-16"
                />
                <span className="text-xs text-fg-subtle">%</span>
                <button
                  onClick={() => removeStop(i)}
                  disabled={stops.length <= 2}
                  className="size-7 rounded text-fg-subtle hover:text-danger hover:bg-surface-hover transition disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card>
        <div className="space-y-2">
          <ExportRow
            label="CSS"
            value={`background: ${css};`}
          />
          <ExportRow
            label="Tailwind"
            value={
              type === 'linear'
                ? `bg-[linear-gradient(${angle}deg,${stopsCss.replace(/\s/g, '_')})]`
                : `bg-[${type}-gradient(${stopsCss.replace(/\s/g, '_')})]`
            }
          />
          <ExportRow label="Raw" value={css} />
        </div>
      </Card>
    </div>
  );
}

function ExportRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-bg-inset border border-border group">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-20 shrink-0 text-2xs uppercase tracking-wider text-fg-subtle font-mono">
          {label}
        </div>
        <code className="font-mono text-xs truncate">{value}</code>
      </div>
      <button
        onClick={async () => {
          const ok = await copyToClipboard(value);
          if (ok) toast.success('Copied');
        }}
        className="opacity-0 group-hover:opacity-100 transition size-7 rounded hover:bg-surface-hover text-fg-subtle hover:text-fg flex items-center justify-center shrink-0"
      >
        <Copy size={11} />
      </button>
    </div>
  );
}

export default GradientBuilder;
